import type { BusinessFunction, CreateBusinessFunctionBody } from '@lib/api-types';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ApiError } from '../api/client.ts';
import { ClusterPatterns } from '../components/ClusterPatterns.tsx';
import {
  useBusinessFunctions,
  useCreateBusinessFunction,
  useDeleteBusinessFunction,
  useUpdateBusinessFunction,
} from '../view-models/business-functions.ts';
import { useFileClusters } from '../view-models/file-clusters.ts';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * Business functions section of /settings/setup — typed end-to-end
 * consumer of the business_functions API. Demonstrates the stack:
 *   - hono/client RPC via `api/client.ts`
 *   - useQuery / useMutation hooks from `view-models/business-functions.ts`
 *   - Per-entity CRUD against the ledger-projection-backed endpoints
 */
export function SettingsBusinessFunctions() {
  const { orgSlug, role } = useOutletContext<SettingsOutletContext>();
  const list = useBusinessFunctions(orgSlug);
  const clusters = useFileClusters(orgSlug);
  const canMutate = role === 'admin';

  return (
    <div className="stack" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <section>
        <h2 style={{ marginBottom: '0.25rem' }}>Business functions</h2>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Customer-visible product surfaces this org accounts against.
          Functions show up as columns in the Resilience matrix.
        </p>
        {list.isPending ? (
          <p className="muted">Loading…</p>
        ) : list.isError ? (
          <ErrorBanner error={list.error} />
        ) : (
          <FunctionsList
            items={list.data}
            orgSlug={orgSlug}
            canMutate={canMutate}
            clusters={clusters.data}
          />
        )}
      </section>
      {canMutate ? (
        <section>
          <h2 style={{ marginBottom: '0.25rem' }}>Add a business function</h2>
          <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            Use a short slug (lowercase, hyphens) — e.g. <code>catalog</code> or{' '}
            <code>image_pipeline</code>.
          </p>
          <CreateForm orgSlug={orgSlug} />
        </section>
      ) : (
        <p className="muted">
          Only org owners can add, rename, or remove business functions.
        </p>
      )}
    </div>
  );
}

function FunctionsList({
  items,
  orgSlug,
  canMutate,
  clusters,
}: {
  items: BusinessFunction[];
  orgSlug: string;
  canMutate: boolean;
  clusters: import('../view-models/file-clusters.ts').FileCluster[] | undefined;
}) {
  if (items.length === 0) {
    return <p className="muted">No business functions yet.</p>;
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {items.map((f) => (
        <FunctionRow
          key={f.id}
          fn={f}
          orgSlug={orgSlug}
          canMutate={canMutate}
          clusters={clusters}
        />
      ))}
    </ul>
  );
}

function FunctionRow({
  fn,
  orgSlug,
  canMutate,
  clusters,
}: {
  fn: BusinessFunction;
  orgSlug: string;
  canMutate: boolean;
  clusters: import('../view-models/file-clusters.ts').FileCluster[] | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [display, setDisplay] = useState(fn.display);
  const [subtitle, setSubtitle] = useState(fn.subtitle ?? '');
  const update = useUpdateBusinessFunction(orgSlug);
  const del = useDeleteBusinessFunction(orgSlug);

  if (editing) {
    return (
      <li style={liStyle}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate(
              {
                id: fn.id,
                body: {
                  display,
                  subtitle: subtitle.trim().length > 0 ? subtitle : null,
                },
              },
              {
                onSuccess: () => setEditing(false),
              },
            );
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <label>
            <span style={labelStyle}>Display name</span>
            <input
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label>
            <span style={labelStyle}>Subtitle</span>
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="One-line summary"
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            {update.isError ? <ErrorBanner error={update.error} /> : null}
          </div>
        </form>
      </li>
    );
  }

  return (
    <li style={liStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <strong>{fn.display}</strong>{' '}
          <code style={{ color: 'var(--color-muted, #888)' }}>{fn.id}</code>
          {fn.subtitle ? (
            <div className="muted" style={{ marginTop: '0.25rem' }}>{fn.subtitle}</div>
          ) : null}
        </div>
        {canMutate ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => { setDisplay(fn.display); setSubtitle(fn.subtitle ?? ''); setEditing(true); }}>
              Edit
            </button>
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (confirm(`Remove "${fn.display}"? Past matrix views still surface it.`)) {
                  del.mutate(fn.id);
                }
              }}
            >
              {del.isPending ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ) : null}
      </div>
      <ClusterPatterns
        clusters={clusters}
        ownerKind="business_function"
        ownerId={fn.id}
      />
      {del.isError ? <ErrorBanner error={del.error} /> : null}
    </li>
  );
}

function CreateForm({ orgSlug }: { orgSlug: string }) {
  const create = useCreateBusinessFunction(orgSlug);
  const [id, setId] = useState('');
  const [display, setDisplay] = useState('');
  const [subtitle, setSubtitle] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const body: CreateBusinessFunctionBody = {
          id: id.trim(),
          display: display.trim(),
          ...(subtitle.trim().length > 0 ? { subtitle: subtitle.trim() } : {}),
        };
        create.mutate(body, {
          onSuccess: () => {
            setId('');
            setDisplay('');
            setSubtitle('');
          },
        });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '40rem' }}
    >
      <label>
        <span style={labelStyle}>Slug</span>
        <input
          value={id}
          onChange={(e) => setId(e.target.value)}
          required
          minLength={1}
          maxLength={120}
          // `-` is escaped so the pattern is unambiguous under the
          // `v` flag the browser uses to compile pattern attributes.
          // Equivalent in meaning to the API-side zod regex
          // `/^[a-z0-9][a-z0-9_-]*$/i`.
          pattern="[A-Za-z0-9][A-Za-z0-9_\-]*"
          placeholder="catalog"
          style={inputStyle}
        />
      </label>
      <label>
        <span style={labelStyle}>Display name</span>
        <input
          value={display}
          onChange={(e) => setDisplay(e.target.value)}
          required
          maxLength={200}
          placeholder="Catalog"
          style={inputStyle}
        />
      </label>
      <label>
        <span style={labelStyle}>Subtitle (optional)</span>
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          maxLength={2000}
          placeholder="One-line summary of the surface"
          style={inputStyle}
        />
      </label>
      <div>
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Adding…' : 'Add business function'}
        </button>
      </div>
      {create.isError ? <ErrorBanner error={create.error} /> : null}
    </form>
  );
}

function ErrorBanner({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.message} (${error.status}${error.code ? ` ${error.code}` : ''})`
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <div role="alert" style={{ color: '#c33', marginTop: '0.5rem' }}>
      {message}
    </div>
  );
}

const liStyle: React.CSSProperties = {
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
  padding: '0.75rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '0.85rem',
  color: 'var(--color-muted, #666)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4rem 0.5rem',
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
};
